import React, { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { InView } from 'react-intersection-observer';

import Paginator from 'dpl/common/design_system/Paginator';
import UntypedTabs from 'dpl/common/design_system/Tabs';
import LoadingWrapper from 'dpl/components/LoadingWrapper';
import OverlayModal from 'dpl/components/OverlayModal';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import { SIMILAR_BREEDS_PAGE } from 'dpl/constants/query_params';
import { setAnalyticsOrigin } from 'dpl/shared/utils/setAnalyticsOrigin';
import PuppyCard from 'dpl/puppy_search_results/components/Card';
import UntypedSearchResultsSkeleton from 'dpl/puppy_search_results/components/skeletons/SearchResultsSkeleton';
import useSimilarBreedSeeAllResults from 'dpl/puppy_search_results/hooks/useSimilarBreedSeeAllResults';
import useSimilarBreedsSeeAll from 'dpl/puppy_search_results/hooks/useSimilarBreedsSeeAll';
import {
  MOBILE_RESULT_GROUP_SIZE,
  sendResultGroupViewedEvent
} from 'dpl/puppy_search_results/utils/resultGroupViewedEvent';
import {
  SEE_ALL_RESULTS_PER_PAGE,
  SIMILAR_BREEDS_RESULTS_TYPES,
  shownBreeds,
  SimilarBreedsResultsType
} from 'dpl/puppy_search_results/utils/similarBreeds';
import {
  collectTrackedExperiments,
  sendRudderstackEvent
} from 'dpl/util/analytics';
import { addToQuery } from 'dpl/util/queryString';

import SimilarBreedsSeeAllBreederCard from './SimilarBreedsSeeAllBreederCard';
import SimilarBreedsSeeAllHeader from './SimilarBreedsSeeAllHeader';

// A `forwardRef` in a .jsx file, so TypeScript infers no props for it. Same
// workaround the similar breeds cards use for `CardMediaCarousel`.
const Tabs = UntypedTabs as React.ComponentType<{
  className?: string | null;
  classes?: Record<string, string | null>;
  activeTabIndex?: number;
  tabItems: React.ReactNode[];
  onTabClick?: (tabIndex: number) => void;
  disabled?: boolean;
}>;

// Another untyped .jsx component, so its props are named here to restore
// checking at this call site.
const SearchResultsSkeleton =
  UntypedSearchResultsSkeleton as React.ComponentType<{
    className?: string | null;
    resultsType: string;
    resultsPerPage: number;
    imageHeight?: string | null;
  }>;

// Enough to run past the fold, not the full page of 40. The visitor only ever
// sees the first screenful of a skeleton, and this remounts on every page and
// tab change.
const SKELETON_CARD_COUNT = 4;

const SEE_ALL_TITLE = 'Similar breed puppies near you';

// What `Breed Searched` calls this surface. The search page behind it reports
// `breed_page`, and this prop is the only thing separating the two.
const SIMILAR_BREED_PAGE = 'similar_breed_page';

// Breeders first, matching the tab order on the search page behind the overlay.
const RESULTS_TYPE_TAB_VALUES: SimilarBreedsResultsType[] = [
  SIMILAR_BREEDS_RESULTS_TYPES.BREEDERS,
  SIMILAR_BREEDS_RESULTS_TYPES.PUPPIES
];

const TAB_ITEMS = ['Breeders', 'Puppies'];

const TAB_CLASSES = {
  list: 'gap-20',
  item: 'w-50',
  tab: 'justify-center tc w-100 pv3 font-16 fw-medium',
  activeTab: 'black',
  inactiveTab: 'tertiary',
  glider: 'bb b--black'
};

interface SimilarBreedsSeeAllOverlayProps {
  /** The module's filters, so the overlay resolves the same selection. */
  filters: Record<string, unknown>;
}

// The paginator's links are advisory - `onPageClick` is what moves the page -
// but an href pointing at `?p=` would paginate the feed behind the overlay if
// the visitor opened it in a new tab.
function pageUrlFor(pageNumber: number) {
  const hash = addToQuery(
    { [SIMILAR_BREEDS_PAGE]: pageNumber > 1 ? pageNumber : undefined },
    window.location.hash
  );

  return `${window.location.pathname}${window.location.search}#${hash}`;
}

/**
 * The "See all" destination, as a full-screen overlay over the search page
 * rather than a page of its own. Being on the same page is what makes the
 * origin context free: the zip, gender and tab the visitor searched with are
 * already in the store, and closing restores the feed exactly as they left it.
 */
export default function SimilarBreedsSeeAllOverlay({
  filters
}: SimilarBreedsSeeAllOverlayProps) {
  const { breedId, breedName } = useSelector(({ server }: any) => server);

  const {
    areTabsAvailable,
    close,
    page,
    resultsType,
    setPage,
    setResultsType
  } = useSimilarBreedsSeeAll();

  const { isLoading, puppies, breeders, totalItemCount, totalPuppyCount } =
    useSimilarBreedSeeAllResults({
      enabled: true,
      filters,
      resultsType,
      page
    });

  const isBreedersTab = resultsType === SIMILAR_BREEDS_RESULTS_TYPES.BREEDERS;

  const results = isBreedersTab ? breeders : puppies;

  // Rank across the whole result set, not within the page - the convention both
  // feeds report (`getPosition` in `SearchResults` and `CardlessSearchResults`).
  // `page_number` travels with the event but the page size does not, and this
  // page serves forty rows where the feed serves twenty, so a per-page position
  // could not be turned back into a rank by anything reading the two together.
  const positionOf = useCallback(
    (idx: number) => (page - 1) * SEE_ALL_RESULTS_PER_PAGE + idx + 1,
    [page]
  );

  // This page is its own search surface, not a corner of the one behind it, so
  // the tab it is on is a `view` rather than a location within another view.
  const view = isBreedersTab
    ? ANALYTICS_VIEWS.SIMILAR_BREED_BREEDER_SEARCH_RESULTS
    : ANALYTICS_VIEWS.SIMILAR_BREED_PUPPY_SEARCH_RESULTS;

  const queryClient = useQueryClient();

  // Grouped in fours as the feed does it, rather than the module's single
  // event: this is an ordinary paginated list, long enough that one event on
  // arrival would report forty rows the visitor mostly never scrolled to.
  const trackGroupInView = useCallback(
    (idx: number) => {
      if (idx % MOBILE_RESULT_GROUP_SIZE !== 0) {
        return;
      }

      sendResultGroupViewedEvent(queryClient, {
        breedId,
        resultsType,
        resultGroup: results.slice(idx, idx + MOBILE_RESULT_GROUP_SIZE),
        position: positionOf(idx),
        page,
        view,
        viewFeature: isBreedersTab
          ? ANALYTICS_VIEW_FEATURES.BREEDER_CARD
          : ANALYTICS_VIEW_FEATURES.PUPPY_CARD,
        viewFeatureLocation: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS,
        // Forty rows a page, and the payload already carries the breakdown the
        // fetch would have gone and got - see the `see_all` breeder partial.
        fetchAvailability: false
      });
    },
    [
      queryClient,
      breedId,
      resultsType,
      results,
      page,
      positionOf,
      view,
      isBreedersTab
    ]
  );

  // A contact is made long after this page is gone - on the profile the visitor
  // clicked through to - and reads `origin_view` back out of local storage
  // then. Set on the click and not on open: the visitor can open this page,
  // close it, and contact someone from the feed behind it, and that contact
  // belongs to the feed.
  // The breeder card writes this in its own click handler; the shared puppy
  // card carries over whatever origin it finds rather than naming its view, so
  // this page has to name itself around that one.
  const setOriginToThisPage = useCallback(() => {
    setAnalyticsOrigin({
      originView: view,
      originViewFeature: ANALYTICS_VIEW_FEATURES.PUPPY_CARD,
      originViewFeatureLocation: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS
    });
  }, [view]);

  const contentRef = useRef<HTMLDivElement>(null);

  // Which page and tab have already been reported. Not the query's status: a
  // page the visitor has been on before comes back from the cache already
  // resolved, so a status that never transitions would drop the event for it.
  const reportedRef = useRef<string | null>(null);

  // A second `Breed Searched`, alongside the one the search page behind fired
  // on load. Opening this page is a search of its own - it is also what stands
  // in for a click event on the CTA, which the spec deliberately omits.
  useEffect(() => {
    if (isLoading) {
      return;
    }

    // A page past the end bounces back to page one below. Reporting it would
    // file a search for a page the visitor is never shown.
    if (page > 1 && !results.length) {
      return;
    }

    const reportKey = `${resultsType}:${page}`;

    if (reportedRef.current === reportKey) {
      return;
    }

    reportedRef.current = reportKey;

    const breeds = shownBreeds(results);

    sendRudderstackEvent(ANALYTICS_EVENTS.BREED_SEARCHED, {
      // The breed the visitor searched, unchanged and still a scalar: this page
      // is reached from it, and it is what joins the two searches together.
      breed_id: breedId,
      breed_name: breedName,
      page: SIMILAR_BREED_PAGE,
      page_number: page,
      results_display_type: resultsType,
      results_size_breeders: totalItemCount,
      results_size_available_puppies: totalPuppyCount,
      // The related breeds this page of results is made of.
      similar_breed_ids: breeds.ids,
      similar_breed_names: breeds.names,
      experiments: collectTrackedExperiments()
    });
  }, [isLoading, page, resultsType, results]);

  // Paging or switching tabs from the bottom of a long list would otherwise
  // land the visitor at the bottom of the next one. `OverlayModal` wraps this
  // content in the element that scrolls, so that is the one to reset.
  useEffect(() => {
    const scrollingEl = contentRef.current?.parentElement;

    if (scrollingEl) {
      scrollingEl.scrollTop = 0;
    }
  }, [page, resultsType]);

  // A hand-typed page past the end comes back empty, and the empty state reads
  // as "nothing near you" rather than "no such page". Land them on page one
  // instead, the way the feed behind the overlay does.
  useEffect(() => {
    if (!isLoading && page > 1 && !results.length) {
      setPage(1);
    }
  }, [isLoading, page, results.length]);

  function handleTabClick(tabIndex: number) {
    setResultsType(RESULTS_TYPE_TAB_VALUES[tabIndex]);
  }

  return (
    <OverlayModal
      className="SimilarBreedsSeeAllOverlay"
      dataTestId="similar-breeds-see-all-overlay"
      hideCloseButton
      onClose={close}
      // Dismissing is the "Return to" control's job. There is no overlay to
      // click past here - the content fills the viewport - and treating a stray
      // tap as a close would lose the visitor's place in the results.
      shouldCloseOnOverlayClick={false}
    >
      <div ref={contentRef} className="SimilarBreedsSeeAll bg-white min-h-100">
        <SimilarBreedsSeeAllHeader
          breedName={breedName}
          onReturnClick={close}
        />
        <div className="bg-sand ph5 pb4">
          <h1 className="font-serif font-32 lh-title">{SEE_ALL_TITLE}</h1>
        </div>
        {areTabsAvailable && (
          <Tabs
            className="bg-sand ph5"
            classes={TAB_CLASSES}
            activeTabIndex={RESULTS_TYPE_TAB_VALUES.indexOf(resultsType)}
            tabItems={TAB_ITEMS}
            onTabClick={handleTabClick}
          />
        )}
        <LoadingWrapper
          // `SearchResultsSkeleton` lays its cards out on the bootstrap grid,
          // whose `row` overhangs its parent by half a 24px gutter. On the
          // puppies tab `ph5` absorbs that and lands the cards at the same 20px
          // as the real ones; on the breeders tab the cards are full-bleed, so
          // the overhang is clipped instead of padded away.
          className={isBreedersTab ? 'overflow-hidden' : 'ph5 pt4'}
          isLoading={isLoading}
          fallback={
            <SearchResultsSkeleton
              resultsType={resultsType}
              resultsPerPage={SKELETON_CARD_COUNT}
            />
          }
        >
          {() => {
            if (!results.length) {
              return (
                <div className="tc pv12 ph5">
                  <p className="font-18 fw-medium mb2">
                    No similar breed puppies nearby
                  </p>
                  <p className="font-16 secondary lh-body">
                    Try widening your search, or check back soon.
                  </p>
                </div>
              );
            }

            return (
              <>
                {isBreedersTab
                  ? breeders.map((breeder, idx) => (
                      // The breeder card has no observer of its own, unlike
                      // the shared puppy card, so the group trigger is wrapped
                      // around it here.
                      <InView
                        key={breeder.id}
                        triggerOnce
                        onChange={inView => {
                          if (inView) trackGroupInView(idx);
                        }}
                      >
                        <SimilarBreedsSeeAllBreederCard
                          breeder={breeder}
                          position={positionOf(idx)}
                          page={page}
                          view={view}
                        />
                      </InView>
                    ))
                  : puppies.map((puppy, idx) => (
                      <div
                        key={puppy.id}
                        className="ph5 pv4"
                        onClickCapture={setOriginToThisPage}
                      >
                        <PuppyCard
                          className="pb3"
                          puppy={puppy}
                          breedName={puppy.breed?.name}
                          trackInView={() => trackGroupInView(idx)}
                          analytics={{
                            view,
                            view_feature_location:
                              ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS,
                            page_number: page,
                            position: positionOf(idx)
                          }}
                          bordered
                        />
                      </div>
                    ))}
                {totalItemCount > SEE_ALL_RESULTS_PER_PAGE && (
                  <Paginator
                    className="tc pv6"
                    currentPage={page}
                    itemsPerPage={SEE_ALL_RESULTS_PER_PAGE}
                    onPageClick={setPage}
                    pageUrlFor={pageUrlFor}
                    totalItemCount={totalItemCount}
                    arrows={false}
                  />
                )}
              </>
            );
          }}
        </LoadingWrapper>
      </div>
    </OverlayModal>
  );
}
